// 방문 추적. 순수 로직은 여기, 전송은 아래 track() 한 줄.
// 추적이 실패해도 사이트는 정상 동작해야 해요 — 모든 실패를 삼킵니다.

export type VisitStage =
  | "landed"
  | "viewed_about"
  | "viewed_portfolio"
  | "portfolio_expanded"
  | "portfolio_link"
  | "viewed_quote"
  | "quote_option"
  | "quote_preset"
  | "quote_computed"
  | "quote_process_opened"
  | "inquiry_opened"
  | "inquiry_submitted";

export interface Utm {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  gclid?: string;
  fbclid?: string;
}

export interface ClientHints {
  viewport_w: number;
  viewport_h: number;
  dpr: number;
  language: string;
  timezone: string;
  color_scheme: "light" | "dark";
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface WindowLike {
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
  navigatorLanguage: string;
  timezone: string;
  prefersDark: boolean;
}

export interface ScrollLike {
  scrollY: number;
  innerHeight: number;
  scrollHeight: number;
}

const SESSION_KEY = "dcc_visit_sid";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const UTM_MAX = 60;
// gclid/fbclid 는 서버가 200까지 허용해요 — 60에서 자르면 실제 구글 클릭 ID가 잘려
// 광고 성과에 매칭이 안 되는, 비어있는 것보다 나쁜 상태가 돼요.
const CLICK_ID_MAX = 200;

const UTM_KEYS: [keyof Utm, string, number][] = [
  ["source", "utm_source", UTM_MAX],
  ["medium", "utm_medium", UTM_MAX],
  ["campaign", "utm_campaign", UTM_MAX],
  ["term", "utm_term", UTM_MAX],
  ["content", "utm_content", UTM_MAX],
  ["gclid", "gclid", CLICK_ID_MAX],
  ["fbclid", "fbclid", CLICK_ID_MAX],
];

/** sessionStorage 를 못 쓰는 환경(사파리 프라이빗 등)에서는 null 을 주고 추적을 끕니다. */
export function getOrCreateSessionId(store: StorageLike): string | null {
  try {
    const existing = store.getItem(SESSION_KEY);
    if (existing && UUID_RE.test(existing)) return existing;
    const created = crypto.randomUUID();
    store.setItem(SESSION_KEY, created);
    return created;
  } catch {
    return null;
  }
}

export function parseUtm(search: string): Utm | null {
  const params = new URLSearchParams(search);
  const utm: Utm = {};
  for (const [field, param, max] of UTM_KEYS) {
    const value = params.get(param);
    if (value) utm[field] = value.slice(0, max);
  }
  return Object.keys(utm).length > 0 ? utm : null;
}

export function collectClient(win: WindowLike): ClientHints {
  return {
    viewport_w: win.innerWidth,
    viewport_h: win.innerHeight,
    dpr: win.devicePixelRatio,
    language: win.navigatorLanguage,
    timezone: win.timezone,
    color_scheme: win.prefersDark ? "dark" : "light",
  };
}

/** 처음 보는 스테이지면 true 를 주고 기록해요. 두 번째부터 false 라 전송을 건너뜁니다. */
export function dedupe(sent: Set<string>, stage: VisitStage, detail?: string): boolean {
  const key = detail ? `${stage}:${detail}` : stage;
  if (sent.has(key)) return false;
  sent.add(key);
  return true;
}

export function scrollPct(win: ScrollLike): number {
  const scrollable = win.scrollHeight - win.innerHeight;
  if (scrollable <= 0) return 100;
  const raw = Math.round((win.scrollY / scrollable) * 100);
  return Math.min(100, Math.max(0, raw));
}

const apiUrl = import.meta.env.VITE_API_URL ?? "";
const sent = new Set<string>();

let sessionId: string | null = null;
let initialized = false;

export interface QuoteTrace {
  config_version: number | null;
  type: string;
  pages: number;
  option_keys: string[];
  support: string;
  standard: number;
  discounted: number;
  monthly: number;
}

function readWindow(): WindowLike {
  return {
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    navigatorLanguage: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    prefersDark: window.matchMedia("(prefers-color-scheme: dark)").matches,
  };
}

/** 문의 제출 body 에 실어 보낼 세션 ID. 추적이 꺼져 있으면 null 이에요. */
export function trackInquirySessionId(): string | null {
  return sessionId;
}

export function track(stage: VisitStage, opts?: { detail?: string; quote?: QuoteTrace }): void {
  // 추적은 절대 사이트를 깨뜨리면 안 돼요 — window.sessionStorage 게터 자체가
  // SecurityError 를 던지는 환경(쿠키 전체 차단, allow-same-origin 없는 iframe 등)이
  // 있어서 함수 전체를 감쌉니다. getOrCreateSessionId 안의 try/catch 는 이미 얻은
  // store 객체에 대한 getItem/setItem 실패만 잡을 뿐, 이 게터 자체는 못 잡아요.
  try {
    // VITE_API_URL 미설정(로컬 web-only)이면 아무것도 하지 않아요.
    if (!apiUrl) return;

    if (!initialized) {
      initialized = true;
      sessionId = getOrCreateSessionId(window.sessionStorage);
    }
    if (!sessionId) return;
    if (!dedupe(sent, stage, opts?.detail)) return;

    // dedupe 가 이미 키를 넣은 뒤라 첫 전송에서만 size 가 1이에요 — meta/utm 은 세션 첫 요청에만 실어요.
    const firstCall = sent.size === 1;
    const body: Record<string, unknown> = {
      session_id: sessionId,
      stage,
      scroll_pct: scrollPct({
        scrollY: window.scrollY,
        innerHeight: window.innerHeight,
        scrollHeight: document.documentElement.scrollHeight,
      }),
    };
    if (opts?.detail) body.detail = opts.detail;
    if (opts?.quote) body.quote = opts.quote;
    if (firstCall) {
      body.meta = { landing_path: window.location.pathname, ...collectClient(readWindow()) };
      const utm = parseUtm(window.location.search);
      if (utm) body.utm = utm;
    }

    // fire-and-forget. keepalive 라 페이지를 떠나도 요청이 살아남아요.
    // sendBeacon 은 content-type 헤더를 못 붙여서 쓰지 않아요.
    void fetch(`${apiUrl}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      // 콘솔도 더럽히지 않아요.
    });
  } catch {
    // 추적 실패가 사이트를 깨뜨리지 않아요.
  }
}
